import React, { createContext, useContext, useMemo, useState } from "react";
import {
  clearAdminSession,
  clearUserSession,
  hasAdminSession,
  hasUserSession,
  migrateLegacySessions,
  setAdminSession,
  setUserSession,
} from "./session";

migrateLegacySessions();

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [userAuthenticated, setUserAuthenticated] = useState(hasUserSession);
  const [adminAuthenticated, setAdminAuthenticated] = useState(hasAdminSession);

  const loginUser = (token, extras = {}) => {
    setUserSession({
      token,
      uid: extras.uid,
      user: extras.user,
      apiKey: extras.apiKey,
      instanceLimit: extras.instanceLimit,
    });
    setUserAuthenticated(true);
  };

  const loginAdmin = (token, extras = {}) => {
    setAdminSession({
      token,
      uid: extras.uid,
      admin: extras.admin || { email: extras.email, role: "admin", uid: extras.uid },
    });
    setAdminAuthenticated(true);
  };

  /** Back-compat: route by extras.role */
  const login = (token, extras = {}) => {
    if (extras.role === "admin") loginAdmin(token, extras);
    else loginUser(token, extras);
  };

  const logoutUser = () => {
    clearUserSession();
    setUserAuthenticated(false);
  };

  const logoutAdmin = () => {
    clearAdminSession();
    setAdminAuthenticated(false);
  };

  /** Prefer scoped logout; full clear only if neither session left. */
  const logout = (which) => {
    if (which === "admin") {
      logoutAdmin();
      return;
    }
    if (which === "user") {
      logoutUser();
      return;
    }
    logoutUser();
    logoutAdmin();
  };

  const role = adminAuthenticated && userAuthenticated
    ? "both"
    : adminAuthenticated
      ? "admin"
      : userAuthenticated
        ? "user"
        : null;

  const value = useMemo(
    () => ({
      isAuthenticated: userAuthenticated || adminAuthenticated,
      userAuthenticated,
      adminAuthenticated,
      role,
      isAdmin: adminAuthenticated,
      isUser: userAuthenticated,
      login,
      loginUser,
      loginAdmin,
      logout,
      logoutUser,
      logoutAdmin,
    }),
    [userAuthenticated, adminAuthenticated, role]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
